import { Component, Node, Button, _decorator } from 'cc';
import DropdownOption from './DropdownOption';
import ItemGenerator from '../Misc/ItemGenerator';
import Page from '../Page/Page';

const { ccclass, property, requireComponent } = _decorator;

enum EventType {
    SELECTED_OPTION_CHANGED = 'SELECTED_OPTION_CHANGED',
    OPTION_CLICKED = 'OPTION_CLICKED',
}

@ccclass('Dropdown')
@requireComponent(Page)
export default class Dropdown<T = any> extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property(Node)
    public readonly displayName: Node = null;

    @property(Node)
    public readonly indicator: Node = null;

    @property(ItemGenerator)
    private readonly optionGenerator: ItemGenerator = null;

    private _page: Page = null;

    private _index: number = 0;
    private _optionValues: T[] = [];
    private _updateDisplayNameCallback: (target: Node, displayName: T) => void = null;

    public get index() {
        return this._index;
    }
    public set index(value: number) {
        this._setIndex(value);
    }

    public get selectedOptionValue() {
        return this._optionValues[this.index];
    }
    public set selectedOptionValue(value: T) {
        this.index = this._optionValues.indexOf(value);
    }

    public get options() {
        return this.optionGenerator.items;
    }

    public get optionValues() {
        return this._optionValues;
    }

    public onLoad() {
        this._page = this.getComponent(Page);

        this._page.node.on(Page.EVENT_TYPE.OPEN_START, () => {
            if (this.indicator) {
                this.indicator.angle = 180;
            }
        });
        this._page.node.on(Page.EVENT_TYPE.CLOSE_END, () => {
            if (this.indicator) {
                this.indicator.angle = 0;
            }
        });
        this.optionGenerator.node.on(ItemGenerator.EVENT_TYPE.ITEM_GENERATED, this._onOptionGenerated, this);
        this.optionGenerator.items.forEach((option) => this._onOptionGenerated(option));
    }

    public init(options: Readonly<T[]>, updateDisplayName: (displayName: Node, selectedOption: T) => void, updateOptionName: (option: Node, value: T) => void) {
        this._updateDisplayNameCallback = updateDisplayName;
        this.optionGenerator.displayItemCount = options.length;
        this._optionValues = [...options];
        options.forEach((option, idx) => {
            const item = this.optionGenerator.items[idx];
            updateOptionName(item, option);
        });
        if (this.index >= options.length) {
            this.index = 0;
        }
        this._onSelectedOptionChanged();
    }

    public setIndexWithoutNotify(value: number) {
        this._setIndex(value, false);
    }

    private _setIndex(value: number, emitEvent = true) {
        if (this._index === value) {
            return;
        }
        this._index = value;
        this._onSelectedOptionChanged();
        if (emitEvent) {
            this.node.emit(EventType.SELECTED_OPTION_CHANGED);
        }
    }

    private _onSelectedOptionChanged() {
        this.options.forEach((option, idx) => {
            const dropdownOption = option.getComponent('DropdownOption') as DropdownOption;
            if (!dropdownOption) {
                return;
            }
            if (idx === this.index) {
                dropdownOption.onSelected();
            } else {
                dropdownOption.onDeselected();
            }
        });
        this._updateDisplayNameCallback(this.displayName, this.selectedOptionValue);
    }

    private _onOptionGenerated(option: Node) {
        option.on(Node.EventType.TOUCH_END, () => {
            const button = option.getComponent(Button);
            if (!button || button.interactable) {
                this.index = option.getSiblingIndex();
                this._page.close();
            }
            this.node.emit(EventType.OPTION_CLICKED, option);
        });
    }
}
